import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useI18n } from "@/i18n";
import { useRouter, type Route } from "@/router/Router";
import { useCart } from "@/state/CartContext";
import { useCatalog } from "@/state/CatalogContext";
import { useConfig } from "@/lib/useConfig";
import { ApiError, cancelOrder, deleteAddress, fetchAddresses, fetchTaste, saveAddress, voteTaste, type TasteProfile, fetchLoyalty, fetchMyReviews, fetchOrder, fetchOrders, logoutWeb, markArriving, mediaUrl, type SavedAddress } from "@/lib/api";
import { clearWebSession, getWebProfile, getWebSession } from "@/lib/webSession";
import { formatMinutesLeft, formatOrderDate, formatUzs } from "@/lib/format";
import { confirmAction } from "@/lib/telegram";
import { OrderJourney, ReceiptLabel } from "@/components/Premium";
import { PaymentStatus } from "@/components/PaymentStatus";
import { PayBadge } from "@/components/PayBadge";
import { GiftShare } from "@/components/GiftShare";
import { ReviewSheet } from "@/components/ReviewSheet";
import { ServiceRating } from "@/components/ServiceRating";
import { MarketingToggle } from "@/components/MarketingSection";
import { BirthdayRow } from "@/components/BirthdaySheet";
import { DI, DProductCard, PATH, WI, dsvg } from "@/components/desktop/desktopUi";
import { NoteIcon, RoastDiamonds } from "@/components/Flavor";
import { ALL_ORIGINS, findOrigin } from "@/components/OriginMap";
import type { FlavorNote, Locale, LoyaltyState, Order } from "@/types";

const ACTIVE: Order["status"][] = ["new", "confirmed", "paid", "preparing", "ready_for_pickup", "handed_to_courier"];
const CANCELLABLE: Order["status"][] = ["new", "confirmed", "paid", "preparing", "ready_for_pickup"];
const REWARD_EVERY = 10;
const LOCALES: Array<[Locale, string]> = [["ru", "Русский"], ["uz", "O'zbekcha"], ["en", "English"]];

const IC = {
  home: dsvg('<path d="M3 11 12 4l9 7M5 10v10h14V10"/>', 19, 1.6),
  orders: dsvg('<path d="M6 7h12l-1 13H7L6 7zM9 7a3 3 0 0 1 6 0"/>', 19, 1.6),
  fav: dsvg('<path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z"/>', 19, 1.6),
  addr: dsvg('<path d="M12 21s-6-5.7-6-11a6 6 0 0 1 12 0c0 5.3-6 11-6 11z"/><circle cx="12" cy="10" r="2.2"/>', 19, 1.6),
  taste: dsvg('<path d="M12 3c4 4 6 7 6 10a6 6 0 0 1-12 0c0-3 2-6 6-10z"/>', 19, 1.6),
  passport: dsvg('<rect x="5" y="3" width="14" height="18" rx="2"/><circle cx="12" cy="10" r="3"/><path d="M9 16h6"/>', 19, 1.6),
  recipes: dsvg('<path d="M5 11h11v3a5 5 0 0 1-5 5h-1a5 5 0 0 1-5-5zM16 12h1.5a2.5 2.5 0 0 1 0 5H15M8 4v4M11 3v5M14 4v4"/>', 19, 1.6),
  faq: dsvg('<circle cx="12" cy="12" r="8.5"/><path d="M9.6 9.6a2.4 2.4 0 1 1 3.4 2.2c-.6.3-1 .9-1 1.6v.4M12 17h.01"/>', 19, 1.6),
  biz: dsvg('<path d="M4 20V8l8-4 8 4v12M9 20v-6h6v6"/>', 19, 1.6),
  bell: dsvg('<path d="M6 16V11a6 6 0 0 1 12 0v5l2 2H4zM10 20a2 2 0 0 0 4 0"/>', 18, 1.6),
  cake: dsvg('<path d="M4 20h16M5 20v-7h14v7M8 13v-2M12 13v-2M16 13v-2M8 8.5a1 1 0 0 0 0-2M12 8.5a1 1 0 0 0 0-2M16 8.5a1 1 0 0 0 0-2"/>', 18, 1.6),
  out: dsvg('<path d="M14 4h5v16h-5M10 8l-4 4 4 4M6 12h10"/>', 19, 1.6),
  tg: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M21.5 4.2 2.9 11.4c-1 .4-1 1.6.1 1.9l4.6 1.5 1.8 5.6c.2.8 1.2 1 1.8.4l2.6-2.4 4.8 3.5c.7.5 1.7.1 1.9-.8l3.2-15.2c.2-1-.8-1.8-1.7-1.5z" /></svg>,
};

type Section = "overview" | "orders" | "favorites" | "addresses" | "taste" | "passport";

const openLogin = () => window.dispatchEvent(new Event("kms:login"));
const isSignedIn = () => !!getWebSession();

/** Status chip colour: green when it is ready/done, grey when cancelled, accent otherwise. */
function StatusChip({ order }: { order: Order }) {
  const { t } = useI18n();
  const tone = order.status === "cancelled" ? "muted" : order.status === "delivered" || order.status === "ready_for_pickup" ? "ok" : "now";
  return <span className="dac-chip" data-tone={tone}>{t(`orders.status.${order.status}`)}</span>;
}

/** "Sign in to see your orders" — shown instead of account pages to a guest. */
function SignInCard() {
  const { t } = useI18n();
  return (
    <div className="dac-card dac-signin">
      <img src="/brand/kms-logo.png" alt="KMS ROASTING" />
      <h2 className="dk-serif">{t("desktop.accSignInTitle")}</h2>
      <p>{t("desktop.accSignInText")}</p>
      <button className="dk-signin dac-signin__btn" onClick={openLogin}>{IC.tg}{t("desktop.signIn")}</button>
    </div>
  );
}

/** Personal account frame: menu on the left, the section on the right. */
export function AccountShell({ section, title, crumbs, children }: { section: Section; title: string; crumbs?: Array<[string, Route?]>; children: React.ReactNode }) {
  const { t } = useI18n();
  const { navigate } = useRouter();
  const profile = getWebProfile();
  const signed = isSignedIn();
  const signOut = async () => {
    await logoutWeb().catch(() => {});
    clearWebSession();
    window.location.reload();
  };
  const { features } = useConfig();
  const items: Array<[Section | "recipes" | "faq" | "b2b", string, React.ReactNode, Route]> = [
    ["overview", t("desktop.accOverview"), IC.home, { name: "profile" }],
    ["orders", t("profile.orders"), IC.orders, { name: "orders" }],
    ["favorites", t("profile.favorites"), IC.fav, { name: "favorites" }],
    ["addresses", t("profile.addresses"), IC.addr, { name: "addresses" }],
    ["taste", t("profile.taste"), IC.taste, { name: "taste" }],
    ...(features.passport ? [["passport", t("profile.passport"), IC.passport, { name: "passport" }] as [Section, string, React.ReactNode, Route]] : []),
    ["recipes", t("profile.recipes"), IC.recipes, { name: "recipes" }],
    ["faq", t("faq.title"), IC.faq, { name: "faq" }],
    ["b2b", t("profile.b2b"), IC.biz, { name: "lead", source: "account" }],
  ];
  const trail = crumbs ?? [[title]];
  return (
    <div className="dac">
      <div className="dc-crumbs">
        <button onClick={() => navigate({ name: "home" })}>{t("desktop.crumbHome")}</button>
        {trail.map(([label, to], i) => (
          <React.Fragment key={i}>
            {" › "}
            {to ? <button onClick={() => navigate(to)}>{label}</button> : <span>{label}</span>}
          </React.Fragment>
        ))}
      </div>
      <div className="dac-layout">
        <aside className="dac-side">
          <div className="dac-me">
            {profile?.photoUrl ? <img src={profile.photoUrl} alt="" /> : <span>{(profile?.firstName ?? "K").charAt(0).toUpperCase()}</span>}
            <div>
              <b>{signed ? profile?.firstName ?? t("profile.guest") : t("profile.guest")}</b>
              <small>{signed && profile?.username ? `@${profile.username}` : t("desktop.accTitle")}</small>
            </div>
          </div>
          <nav className="dac-nav">
            {items.map(([key, label, icon, to]) => (
              <button key={key} data-active={key === section} onClick={() => navigate(to)}>
                {icon}
                <span>{label}</span>
              </button>
            ))}
            {signed && (
              <button className="dac-nav__out" onClick={signOut}>
                {IC.out}
                <span>{t("desktop.signOut")}</span>
              </button>
            )}
          </nav>
        </aside>
        <section className="dac-main">{signed ? children : <SignInCard />}</section>
      </div>
    </div>
  );
}

/** Order thumbnails from the catalogue (orders keep names, not photos). */
function useOrderImages() {
  const { products } = useCatalog();
  return useCallback((order: Order) => order.items.map((i) => products.find((p) => p.id === i.productId)?.images[0]).filter((x): x is string => !!x), [products]);
}

/** One order as a row: photos, number and date, what's inside, status, total. */
function OrderRow({ order }: { order: Order }) {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const images = useOrderImages()(order);
  const unpaid = order.status !== "cancelled" && order.paymentStatus === "pending" && order.paymentMethod !== "cash_on_pickup";
  return (
    <button className="dao-row" onClick={() => navigate({ name: "orderDetail", orderId: order.id })}>
      <span className="dao-row__thumbs">
        {images.slice(0, 3).map((src, i) => <img key={i} src={mediaUrl(src, 200)} alt="" />)}
        {images.length === 0 && <i>{WI(PATH.bean, 26)}</i>}
      </span>
      <span className="dao-row__main">
        <b>№ {order.number}</b>
        <small>{formatOrderDate(order.createdAt, locale)}</small>
        <em>{order.items.map((i) => (i.quantity > 1 ? `${i.productName} × ${i.quantity}` : i.productName)).join(", ")}</em>
      </span>
      <span className="dao-row__state">
        <StatusChip order={order} />
        {unpaid && <span className="dac-chip" data-tone="warn">{t("orders.paymentStatus.pending")}</span>}
      </span>
      <span className="dao-row__sum dk-serif">{formatUzs(order.totalUzs, locale)}</span>
      <span className="dao-row__go">{DI.chevR}</span>
    </button>
  );
}

/** Ещё N заказов — Russian plural forms. */
function ordersLeft(n: number, locale: Locale, t: (k: string, v?: Record<string, string | number>) => string): string {
  if (locale === "en") return t(n === 1 ? "profile.loyaltyLeft_one" : "profile.loyaltyLeft", { count: n });
  if (locale !== "ru") return t("profile.loyaltyLeft", { count: n });
  const m10 = n % 10, m100 = n % 100;
  const form = m10 === 1 && m100 !== 11 ? "one" : m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14) ? "few" : "many";
  return t(`profile.loyaltyLeft_${form}`, { count: n });
}

/** Account overview: greeting, counters, loyalty, recent orders, settings. */
export function DesktopProfile() {
  const { t, locale, setLocale } = useI18n();
  const { navigate } = useRouter();
  const { features } = useConfig();
  const { favorites } = useCart();
  const [loyalty, setLoyalty] = useState<LoyaltyState | null>(null);
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [addresses, setAddresses] = useState<SavedAddress[] | null>(null);
  const signed = isSignedIn();
  useEffect(() => {
    if (!signed) return;
    fetchLoyalty().then(setLoyalty).catch(() => setLoyalty(null));
    fetchOrders().then(setOrders).catch(() => setOrders([]));
    fetchAddresses().then(setAddresses).catch(() => setAddresses([]));
  }, [signed, locale]);
  const name = getWebProfile()?.firstName ?? t("profile.guest");
  const active = (orders ?? []).filter((o) => ACTIVE.includes(o.status));
  const done = loyalty ? (loyalty.hasRewardAvailable ? REWARD_EVERY : Math.max(0, REWARD_EVERY - loyalty.ordersUntilReward)) : 0;

  return (
    <AccountShell section="overview" title={t("desktop.accTitle")}>
      <div className="dac-head">
        <h1 className="dk-serif">{t("desktop.accHello", { name })}</h1>
        <p>{t("desktop.accHelloText")}</p>
      </div>
      <div className="dac-stats">
        <button className="dac-card dac-stat" onClick={() => navigate({ name: "orders" })}>
          <span className="dac-stat__ic">{IC.orders}</span>
          <b className="dk-serif">{orders ? active.length : "…"}</b>
          <small>{t("desktop.accActiveOrders")}</small>
        </button>
        <button className="dac-card dac-stat" onClick={() => navigate({ name: "favorites" })}>
          <span className="dac-stat__ic">{IC.fav}</span>
          <b className="dk-serif">{(favorites ?? []).length}</b>
          <small>{t("profile.favorites")}</small>
        </button>
        <button className="dac-card dac-stat" onClick={() => navigate({ name: "addresses" })}>
          <span className="dac-stat__ic">{IC.addr}</span>
          <b className="dk-serif">{addresses ? addresses.length : "…"}</b>
          <small>{t("profile.addresses")}</small>
        </button>
      </div>

      {loyalty && (
        <div className="dac-loy">
          <div>
            <small>{t("profile.loyaltyTitle")}</small>
            <b className="dk-serif">{done} / {REWARD_EVERY}</b>
            <span>{loyalty.hasRewardAvailable ? t("home.loyaltyReady") : ordersLeft(loyalty.ordersUntilReward, locale, t)}</span>
          </div>
          <div className="dac-loy__dia" aria-hidden="true">
            {Array.from({ length: REWARD_EVERY }, (_, i) => <i key={i} className={i < done ? "on" : ""} />)}
          </div>
        </div>
      )}

      <div className="dac-sec">
        <h2 className="dk-serif">{t("desktop.accRecent")}</h2>
        {orders && orders.length > 0 && <button className="dk-more" onClick={() => navigate({ name: "orders" })}>{t("desktop.accAllOrders")} {DI.arrow}</button>}
      </div>
      {orders === null ? (
        <div className="dac-card dao-list"><div className="dk-sk" style={{ height: 72, margin: 16 }} /></div>
      ) : orders.length === 0 ? (
        <div className="dac-card dac-empty">
          <p>{t("profile.ordersNone")}</p>
          <button className="dk-btn" onClick={() => navigate({ name: "catalog" })}>{t("desktop.allCatalog")} {DI.arrow}</button>
        </div>
      ) : (
        <div className="dac-card dao-list">{orders.slice(0, 3).map((o) => <OrderRow key={o.id} order={o} />)}</div>
      )}

      <div className="dac-sec"><h2 className="dk-serif">{t("profile.groupSettings")}</h2></div>
      <div className="dac-card dac-settings">
        <div className="dac-setting">
          <span>{t("profile.language")}</span>
          <div className="dac-langs">
            {LOCALES.map(([l, label]) => (
              <button key={l} data-active={l === locale} onClick={() => setLocale(l)}>{label}</button>
            ))}
          </div>
        </div>
        <div className="dac-embed-row"><MarketingToggle variant="row" icon={IC.bell} /></div>
        {features.birthday && <div className="dac-embed-row"><BirthdayRow variant="row" icon={IC.cake} /></div>}
      </div>
    </AccountShell>
  );
}

/** All orders with an "active / all" switch. */
export function DesktopOrders() {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [tab, setTab] = useState<"active" | "all">("all");
  const signed = isSignedIn();
  useEffect(() => {
    if (signed) fetchOrders().then(setOrders).catch(() => setOrders([]));
  }, [signed, locale]);
  const active = (orders ?? []).filter((o) => ACTIVE.includes(o.status));
  const shown = tab === "active" ? active : orders ?? [];
  return (
    <AccountShell section="orders" title={t("orders.title")} crumbs={[[t("desktop.accTitle"), { name: "profile" }], [t("orders.title")]]}>
      <div className="dac-head dac-head--row">
        <h1 className="dk-serif">{t("orders.title")}</h1>
        <div className="dh-tabs">
          <button data-active={tab === "all"} onClick={() => setTab("all")}>{t("desktop.accAll")} {orders ? `(${orders.length})` : ""}</button>
          <button data-active={tab === "active"} onClick={() => setTab("active")}>{t("desktop.accActive")} {orders ? `(${active.length})` : ""}</button>
        </div>
      </div>
      {orders === null ? (
        <div className="dac-card dao-list">{[0, 1, 2].map((i) => <div key={i} className="dk-sk" style={{ height: 72, margin: 16 }} />)}</div>
      ) : shown.length === 0 ? (
        <div className="dac-card dac-empty">
          <p>{tab === "active" ? t("desktop.accNoActive") : t("orders.empty")}</p>
          <button className="dk-btn" onClick={() => navigate({ name: "catalog" })}>{t("desktop.allCatalog")} {DI.arrow}</button>
        </div>
      ) : (
        <div className="dac-card dao-list">{shown.map((o) => <OrderRow key={o.id} order={o} />)}</div>
      )}
    </AccountShell>
  );
}

/** Lines, totals and payment/delivery facts shared by the order page and the "thank you" page. */
function OrderLines({ order }: { order: Order }) {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const { products } = useCatalog();
  return (
    <div className="dac-card dod-lines">
      {order.items.map((item, i) => {
        const p = products.find((x) => x.id === item.productId);
        return (
          <div key={i} className="dod-line">
            <button className="dod-line__img" onClick={() => p && navigate({ name: "product", slug: p.slug })} disabled={!p}>
              {p?.images[0] ? <img src={mediaUrl(p.images[0], 200)} alt="" /> : WI(PATH.bean, 26)}
            </button>
            <span className="dod-line__name">
              <b>{item.productName}</b>
              <small>
                {item.variantLabel !== "Стандарт" ? `${item.variantLabel} · ` : ""}× {item.quantity}
                {item.bundleTitle ? ` · ${t("bundles.inSet", { title: item.bundleTitle })}` : ""}
              </small>
            </span>
            <span className="dod-line__sum">{formatUzs(item.priceUzs * item.quantity, locale)}</span>
          </div>
        );
      })}
      <div className="dod-totals">
        <div><span>{t("desktop.goods", { count: order.items.reduce((n, i) => n + i.quantity, 0) })}</span><b>{formatUzs(order.subtotalUzs, locale)}</b></div>
        {order.deliveryFeeUzs > 0 && <div><span>{t("checkout.deliveryLine")}</span><b>{formatUzs(order.deliveryFeeUzs, locale)}</b></div>}
        {order.discountUzs > 0 && <div className="dod-minus"><span>{t("checkout.discountLine", { code: order.promoCode ?? "" })}</span><b>− {formatUzs(order.discountUzs, locale)}</b></div>}
        {(order.giftCardUzs ?? 0) > 0 && <div className="dod-minus"><span>{t("cert.lineShort")}</span><b>− {formatUzs(order.giftCardUzs ?? 0, locale)}</b></div>}
        <div className="dod-total"><span>{t("cart.total")}</span><b className="dk-serif">{formatUzs(order.totalUzs, locale)}</b></div>
      </div>
    </div>
  );
}

/** Payment method, delivery method, address and slot. */
function OrderFacts({ order }: { order: Order }) {
  const { t, locale } = useI18n();
  const payLabel = order.paymentMethod === "cash_on_pickup" ? t("checkout.cashOnPickup") : order.paymentMethod.charAt(0).toUpperCase() + order.paymentMethod.slice(1);
  return (
    <div className="dac-card dod-facts">
      <div className="dod-fact">
        <small>{t("checkout.paymentMethod")}</small>
        <span className="dod-pay"><PayBadge method={order.paymentMethod} /><b>{payLabel}</b></span>
        <em data-tone={order.paymentStatus === "paid" ? "ok" : order.paymentStatus === "pending" ? "warn" : "muted"}>{t(`orders.paymentStatus.${order.paymentStatus}`)}</em>
      </div>
      <div className="dod-fact">
        <small>{t("checkout.deliveryMethod")}</small>
        <b>{order.deliveryMethod === "pickup" ? t("checkout.pickup") : order.deliveryMethod === "bts" ? t("checkout.bts") : t("checkout.yandex")}</b>
        {order.deliveryMethod === "pickup" ? (
          <span>{t("checkout.pickupAddress")}<br />{t("checkout.pickupHours")}</span>
        ) : order.address ? (
          <span>
            {order.address.fullName} · {order.address.phone}
            <br />
            {order.address.address}
            {order.address.comment ? <><br /><i>{order.address.comment}</i></> : null}
          </span>
        ) : null}
        {order.deliverySlot && order.deliveryDate && (
          <span>
            {t("delivery.when")}:{" "}
            {new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "ru-RU", { weekday: "short", day: "numeric", month: "long", timeZone: "Asia/Tashkent" }).format(new Date(order.deliveryDate))}, {order.deliverySlot}
          </span>
        )}
      </div>
    </div>
  );
}

/** One order: timeline and lines on the left; payment, delivery and actions on the right. */
export function DesktopOrderDetail({ orderId }: { orderId: string }) {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const { addItem } = useCart();
  const [order, setOrder] = useState<Order | null | undefined>(undefined);
  const [now, setNow] = useState(new Date());
  const [error, setError] = useState<string | null>(null);
  const [arriving, setArriving] = useState(false);
  const [reviewed, setReviewed] = useState<Record<string, string>>({});
  const [reviewProduct, setReviewProduct] = useState<{ id: string; name: string } | null>(null);
  const signed = isSignedIn();

  const load = useCallback(() => {
    fetchOrder(orderId).then((o) => setOrder(o ?? null)).catch(() => setOrder(null));
  }, [orderId]);
  useEffect(() => {
    if (signed) load();
  }, [load, signed]);
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  useEffect(() => {
    if (order?.status !== "delivered") return;
    fetchMyReviews().then((list) => setReviewed(Object.fromEntries(list.map((r) => [r.productId, r.status])))).catch(() => {});
  }, [order?.status]);

  const crumbs: Array<[string, Route?]> = [[t("desktop.accTitle"), { name: "profile" }], [t("orders.title"), { name: "orders" }], [order ? `№ ${order.number}` : "…"]];
  if (order === undefined || order === null) {
    return (
      <AccountShell section="orders" title={t("orders.title")} crumbs={crumbs}>
        {order === null ? (
          <div className="dac-card dac-empty"><p>{t("common.error")}</p><button className="dk-btn" onClick={() => navigate({ name: "orders" })}>{t("orders.title")}</button></div>
        ) : (
          <div className="dod">
            <div><div className="dk-sk" style={{ height: 44, width: "50%" }} /><div className="dk-sk" style={{ height: 260, marginTop: 20, borderRadius: 16 }} /></div>
            <div className="dk-sk" style={{ height: 320, borderRadius: 16 }} />
          </div>
        )}
      </AccountShell>
    );
  }

  const minutesLeft = formatMinutesLeft(order.cancellableUntil, now);
  const canCancel = CANCELLABLE.includes(order.status) && minutesLeft > 0;
  const uniqueProducts = Array.from(new Map<string, Order["items"][number]>(order.items.map((i: Order["items"][number]) => [i.productId, i] as const)).values());
  const cancel = async () => {
    if (!(await confirmAction(t("orders.cancelConfirm", { id: order.number })))) return;
    try {
      await cancelOrder(order.id);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error"));
    }
  };
  const reorder = () => {
    order.items.forEach((item) => addItem(item.productId, item.variantId, item.quantity));
    navigate({ name: "cart" });
  };

  return (
    <AccountShell section="orders" title={t("orders.title")} crumbs={crumbs}>
      <div className="dac-head dac-head--row">
        <div>
          <h1 className="dk-serif">{t("desktop.accOrderTitle", { id: order.number })}</h1>
          <p>{formatOrderDate(order.createdAt, locale)}</p>
        </div>
        <StatusChip order={order} />
      </div>
      <div className="dod">
        <div className="dod-left">
          <div className="dac-card dod-journey">
            <h3 className="dk-serif">{t("journey.title")}</h3>
            <OrderJourney order={order} />
            {order.status === "cancelled" && order.cancelReason && <p className="dod-note">{t("orders.reason", { reason: order.cancelReason })}</p>}
          </div>
          <OrderLines order={order} />
          {order.status === "delivered" && (
            <div className="dac-card dod-reviews">
              <h3 className="dk-serif">{t("product.reviewsTitle")}</h3>
              {uniqueProducts.map((item) => (
                <div key={item.productId} className="dod-review">
                  <span>{item.productName}</span>
                  {reviewed[item.productId] ? (
                    <span className="dac-chip" data-tone="ok">{reviewed[item.productId] === "approved" ? t("reviews.approved") : t("reviews.pending")}</span>
                  ) : (
                    <button className="dk-btn dk-btn--ghost dod-small" onClick={() => setReviewProduct({ id: item.productId, name: item.productName })}>{t("reviews.leave")}</button>
                  )}
                </div>
              ))}
            </div>
          )}
          {order.status === "delivered" && !order.feedbackRating && <ServiceRating orderId={order.id} onDone={(rating) => setOrder({ ...order, feedbackRating: rating })} />}
        </div>
        <aside className="dod-right">
          {order.status !== "cancelled" && order.paymentStatus !== "paid" && <PaymentStatus order={order} onSettled={load} />}
          <OrderFacts order={order} />
          {order.deliveryMethod === "pickup" && (order.status === "preparing" || order.status === "ready_for_pickup") && (
            <div className="dac-card dod-arrive">
              {order.arrivingAt ? (
                <>
                  <b>◆ {t("arriving.doneTitle")}</b>
                  <span>{t("arriving.doneBody")}</span>
                </>
              ) : (
                <>
                  <b>{t("arriving.title")}</b>
                  <span>{t("arriving.body")}</span>
                  <button
                    className="dk-btn"
                    disabled={arriving}
                    onClick={async () => {
                      setArriving(true);
                      try {
                        const r = await markArriving(order.id);
                        setOrder({ ...order, arrivingAt: r.arrivingAt });
                      } catch (err) {
                        setError(err instanceof ApiError ? err.message : t("common.error"));
                      } finally {
                        setArriving(false);
                      }
                    }}
                  >
                    {t("arriving.button")}
                  </button>
                </>
              )}
            </div>
          )}
          <GiftShare order={order} />
          <div className="dac-card dod-actions">
            <button className="dk-btn" onClick={reorder}>{t("orders.reorder")} {DI.arrow}</button>
            {canCancel ? (
              <>
                <button className="dod-cancel" onClick={cancel}>{t("orders.cancel")}</button>
                <small>{t("orders.cancelledIn", { minutes: minutesLeft })}</small>
              </>
            ) : (
              order.status !== "cancelled" && order.status !== "delivered" && <small>{t("orders.cancelExpired")}</small>
            )}
            {error && <div className="form-error-banner">{error}</div>}
            <button className="dod-help" onClick={() => navigate({ name: "faq", category: "returns" })}>{t("faq.orderHelp")} {DI.chevR}</button>
          </div>
        </aside>
      </div>
      <ReviewSheet product={reviewProduct} open={!!reviewProduct} onClose={() => setReviewProduct(null)} onSent={(productId) => setReviewed((prev) => ({ ...prev, [productId]: "pending" }))} />
    </AccountShell>
  );
}

/** "Спасибо, заказ оформлен": the big moment after checkout. */
export function DesktopOrderDone({ orderId }: { orderId: string }) {
  const { t } = useI18n();
  const { navigate } = useRouter();
  const [order, setOrder] = useState<Order | null>(null);
  const load = useCallback(() => {
    fetchOrder(orderId).then((o) => setOrder(o ?? null)).catch(() => {});
  }, [orderId]);
  useEffect(() => load(), [load]);
  return (
    <div className="ddn">
      <div className="ddn-hero">
        <svg className="ddn-check" width="84" height="84" viewBox="0 0 84 84" aria-hidden="true">
          <circle cx="42" cy="42" r="38" />
          <path d="M26 43l11 11 21-23" />
        </svg>
        <div className="dk-eyebrow">KMS ROASTING</div>
        <h1 className="dk-serif">{t("desktop.doneTitle")}</h1>
        <p>{t("orderConfirmation.number", { id: order?.number ?? "…" })}</p>
        <p className="ddn-sub">
          {order ? (order.deliveryMethod === "pickup" ? t("orderConfirmation.pickupSummary", { address: t("checkout.pickupAddress") }) : t("orderConfirmation.deliverySummary")) : ""}
        </p>
      </div>
      {order ? (
        <div className="ddn-grid">
          <div className="ddn-receipt">
            <ReceiptLabel order={order} />
          </div>
          <div className="ddn-side">
            <PaymentStatus order={order} onSettled={load} />
            {order.isGift && <GiftShare order={order} />}
            <div className="dac-card dod-journey">
              <h3 className="dk-serif">{t("journey.title")}</h3>
              <OrderJourney order={order} />
            </div>
            <OrderFacts order={order} />
            <p className="ddn-note">{t("orderConfirmation.cancelWindow")}</p>
            <div className="ddn-actions">
              <button className="dk-btn" onClick={() => navigate({ name: "orderDetail", orderId })}>{t("orderConfirmation.viewOrder")} {DI.arrow}</button>
              <button className="dk-btn dk-btn--ghost" onClick={() => navigate({ name: "catalog" })}>{t("orderConfirmation.continueShopping")}</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="ddn-grid">
          <div className="dk-sk" style={{ height: 420, borderRadius: 16 }} />
          <div className="dk-sk" style={{ height: 420, borderRadius: 16 }} />
        </div>
      )}
    </div>
  );
}

/** Favourites as a product grid. */
export function DesktopFavorites() {
  const { t } = useI18n();
  const { navigate } = useRouter();
  const { favorites } = useCart();
  const { products } = useCatalog();
  const list = useMemo(() => products.filter((p) => (favorites ?? []).includes(p.id)), [products, favorites]);
  return (
    <AccountShell section="favorites" title={t("profile.favorites")} crumbs={[[t("desktop.accTitle"), { name: "profile" }], [t("profile.favorites")]]}>
      <div className="dac-head">
        <h1 className="dk-serif">{t("profile.favorites")}</h1>
      </div>
      {list.length === 0 ? (
        <div className="dac-card dac-empty">
          <p>{t("profile.favoritesEmpty")}. {t("profile.favNone")}.</p>
          <button className="dk-btn" onClick={() => navigate({ name: "catalog" })}>{t("desktop.allCatalog")} {DI.arrow}</button>
        </div>
      ) : (
        <div className="dac-fav">{list.map((p) => <DProductCard key={p.id} product={p} />)}</div>
      )}
    </AccountShell>
  );
}

/* ───────────── Addresses ───────────── */

const EMPTY_ADDR = { label: "", fullName: "", phone: "", address: "", comment: "" };

export function DesktopAddresses() {
  const { t } = useI18n();
  const [list, setList] = useState<SavedAddress[] | null>(null);
  const [form, setForm] = useState<typeof EMPTY_ADDR | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const signed = isSignedIn();
  const load = useCallback(() => fetchAddresses().then(setList).catch(() => setList([])), []);
  useEffect(() => {
    if (signed) load();
  }, [signed, load]);
  const remove = async (a: SavedAddress) => {
    if (!(await confirmAction(t("addresses.confirmDelete", { label: a.label })))) return;
    await deleteAddress(a.id).catch(() => {});
    load();
  };
  const save = async () => {
    if (!form) return;
    if (!form.fullName.trim() || form.phone.replace(/\D/g, "").length < 9 || !form.address.trim()) return setError(t("gift.fillAll"));
    setBusy(true);
    setError(null);
    try {
      await saveAddress({ label: form.label.trim() || t("addresses.defaultLabel"), fullName: form.fullName.trim(), phone: form.phone.trim(), address: form.address.trim(), comment: form.comment.trim() || null });
      setForm(null);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  };
  const field = (k: keyof typeof EMPTY_ADDR, label: string, ph?: string, wide?: boolean) => (
    <label className="dx-field" style={wide ? { gridColumn: "1 / -1" } : undefined}>
      <span>{label}</span>
      <input value={form![k]} placeholder={ph} onChange={(e) => setForm({ ...form!, [k]: e.target.value })} />
    </label>
  );
  return (
    <AccountShell section="addresses" title={t("profile.addresses")} crumbs={[[t("desktop.accTitle"), { name: "profile" }], [t("profile.addresses")]]}>
      <div className="dac-head dac-head--row">
        <div>
          <h1 className="dk-serif">{t("addresses.title")}</h1>
          <p>{t("dx.addrLead")}</p>
        </div>
        {!form && <button className="dk-btn" onClick={() => { setForm(EMPTY_ADDR); setError(null); }}>+ {t("dx.addrAdd")}</button>}
      </div>
      {form && (
        <div className="dac-card dx-addr-form">
          <h3 className="dk-serif">{t("dx.addrAdd")}</h3>
          <div className="dx-two">
            {field("label", t("dx.addrLabel"), t("addresses.labelPh"), true)}
            {field("fullName", t("checkout.fullName"))}
            {field("phone", t("checkout.phone"), "+998 90 123 45 67")}
            {field("address", t("checkout.address"), undefined, true)}
            {field("comment", t("checkout.comment"), undefined, true)}
          </div>
          {error && <div className="dx-error">{error}</div>}
          <div className="dx-addr-form__btns">
            <button className="dk-btn" disabled={busy} onClick={save}>{busy ? t("common.loading") : t("dx.addrSave")}</button>
            <button className="dk-btn dk-btn--ghost" onClick={() => setForm(null)}>{t("dx.cancel")}</button>
          </div>
        </div>
      )}
      {list === null ? (
        <div className="dx-addrs">{[0, 1].map((i) => <div key={i} className="dk-sk" style={{ height: 150, borderRadius: 16 }} />)}</div>
      ) : list.length === 0 && !form ? (
        <div className="dac-card dac-empty">
          {WI(PATH.home, 54)}
          <p><b className="dk-serif" style={{ fontSize: 22, color: "var(--dk-navy)" }}>{t("addresses.empty")}</b></p>
          <p>{t("dx.addrEmpty")}</p>
          <button className="dk-btn" onClick={() => setForm(EMPTY_ADDR)}>+ {t("dx.addrAdd")}</button>
        </div>
      ) : (
        <div className="dx-addrs">
          {list.map((a) => (
            <div key={a.id} className="dac-card dx-addr">
              <div className="dx-addr__top">
                <span className="dx-addr__ic">{IC.addr}</span>
                <b className="dk-serif">{a.label}</b>
                <button className="dx-link dx-link--danger" onClick={() => remove(a)}>{t("addresses.delete")}</button>
              </div>
              <p className="dx-addr__line">{a.address}</p>
              <p className="dx-addr__who">{a.fullName} · {a.phone}</p>
              {a.comment && <p className="dx-addr__note">{a.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </AccountShell>
  );
}

/* ───────────── Taste ───────────── */

export function DesktopTaste() {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const [data, setData] = useState<TasteProfile | null | undefined>(undefined);
  const signed = isSignedIn();
  const load = useCallback(() => fetchTaste().then(setData).catch(() => setData(null)), []);
  useEffect(() => {
    if (signed) load();
  }, [signed, locale, load]);
  const vote = async (id: string, liked: boolean) => {
    await voteTaste(id, liked).catch(() => {});
    load();
  };
  const left = data ? Math.max(0, data.minLikes - data.likes) : 0;
  const progress = data ? Math.min(1, data.likes / Math.max(1, data.minLikes)) : 0;
  return (
    <AccountShell section="taste" title={t("profile.taste")} crumbs={[[t("desktop.accTitle"), { name: "profile" }], [t("profile.taste")]]}>
      <div className="dac-head">
        <h1 className="dk-serif">{t("taste.title")}</h1>
        <p>{t("dx.tasteLead")}</p>
      </div>
      {data === undefined ? (
        <div className="dk-sk" style={{ height: 180, borderRadius: 18 }} />
      ) : data === null ? (
        <div className="dac-card dac-empty"><p>{t("common.error")}</p></div>
      ) : (
        <>
          <div className="dx-taste-hero">
            <div>
              <small>{t("taste.eyebrow")}</small>
              <b className="dk-serif">{data.archetype ? t(`taste.archetypes.${data.archetype}.name`) : t("taste.notYet")}</b>
              <span>{data.archetype ? t(`taste.archetypes.${data.archetype}.text`) : t("taste.notYetText", { n: left })}</span>
            </div>
            <div className="dx-taste-hero__meter">
              <svg viewBox="0 0 120 120" aria-hidden="true">
                <circle cx="60" cy="60" r="50" />
                <circle cx="60" cy="60" r="50" className="on" strokeDasharray={314} strokeDashoffset={314 * (1 - (data.archetype ? 1 : progress))} />
              </svg>
              <span><b className="dk-serif">{data.likes}</b><small>👍</small></span>
            </div>
          </div>

          <div className="dx-taste">
            <div className="dac-card dx-taste__col">
              <h2 className="dk-serif">{t("taste.rateTitle")}</h2>
              {data.toRate.length === 0 ? (
                <div className="dx-taste__none">
                  <p>{t("taste.emptyText")}</p>
                  <button className="dk-btn dk-btn--ghost" onClick={() => navigate({ name: "catalog", category: "coffee" })}>{t("desktop.allCoffee")} {DI.arrow}</button>
                </div>
              ) : (
                data.toRate.map((p) => (
                  <div key={p.id} className="dx-rate">
                    <button className="dx-rate__img" onClick={() => navigate({ name: "product", slug: p.slug })}>{p.images[0] && <img src={mediaUrl(p.images[0], 200)} alt="" />}</button>
                    <span className="dx-rate__name"><b>{p.name}</b><small>{t("taste.liked")}</small></span>
                    <button className="dx-rate__btn" onClick={() => vote(p.id, true)} aria-label={t("taste.yes")}>👍</button>
                    <button className="dx-rate__btn" onClick={() => vote(p.id, false)} aria-label={t("taste.no")}>👎</button>
                  </div>
                ))
              )}
            </div>
            <div className="dac-card dx-taste__col">
              <h2 className="dk-serif">{t("taste.whatYouLike")}</h2>
              {data.likes === 0 ? (
                <div className="dx-taste__none"><p>{t("taste.emptyTitle")}. {t("dx.tasteHow")}</p></div>
              ) : (
                <>
                  {data.roast !== null && (
                    <div className="dx-taste__roast"><span>{t("taste.roast")}</span><RoastDiamonds level={Math.round(data.roast)} withLabel /></div>
                  )}
                  <div className="dx-scales">
                    {(["acidity", "sweetness", "body", "bitterness"] as const).map((k) =>
                      data.scales[k] === null ? null : (
                        <div key={k}>
                          <span>{t(`flavor.${k}`)}</span>
                          <i><em style={{ width: `${(data.scales[k]! / 5) * 100}%` }} /></i>
                        </div>
                      ),
                    )}
                  </div>
                  {data.notes.length > 0 && (
                    <div className="dx-notes">
                      {data.notes.map((n) => (
                        <span key={n.note}><NoteIcon note={n.note as FlavorNote} />{t(`flavor.notes.${n.note}`)}<small>{n.count}</small></span>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <p className="dx-fine">{t("taste.privacy", { likes: data.likes, dislikes: data.dislikes })}</p>
        </>
      )}
    </AccountShell>
  );
}

/* ───────────── Coffee passport ───────────── */

const REGION: Record<string, "africa" | "americas" | "asia"> = {
  Ethiopia: "africa", Kenya: "africa", Rwanda: "africa", Burundi: "africa", Uganda: "africa", Tanzania: "africa", Yemen: "asia",
  Brazil: "americas", Colombia: "americas", Peru: "americas", Bolivia: "americas", Ecuador: "americas", Guatemala: "americas", Honduras: "americas",
  "Costa Rica": "americas", "El Salvador": "americas", Nicaragua: "americas", Panama: "americas", Mexico: "americas",
  Indonesia: "asia", Vietnam: "asia", India: "asia", "Papua New Guinea": "asia", China: "asia",
};

export function DesktopPassport() {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const { products } = useCatalog();
  const [orders, setOrders] = useState<Order[] | null>(null);
  const signed = isSignedIn();
  useEffect(() => {
    if (signed) fetchOrders().then(setOrders).catch(() => setOrders([]));
  }, [signed]);
  const stamps = useMemo(() => {
    const got = new Map<string, { date: string; product: string }>();
    for (const o of (orders ?? []).filter((x) => x.status === "delivered").sort((a, b) => a.createdAt.localeCompare(b.createdAt))) {
      for (const i of o.items) {
        const p = products.find((x) => x.id === i.productId);
        const origin = findOrigin(p?.attributes?.["Происхождение"] ?? "");
        if (origin && !got.has(origin.en)) got.set(origin.en, { date: o.createdAt, product: i.productName });
      }
    }
    return got;
  }, [orders, products]);
  const count = stamps.size;
  const regions = new Set([...stamps.keys()].map((k) => REGION[k]));
  const badges = [
    { key: "first", ok: count >= 1, icon: PATH.award },
    { key: "explorer", ok: count >= 3, icon: PATH.leaf },
    { key: "traveler", ok: count >= 5, icon: PATH.truck },
    { key: "world", ok: regions.size >= 3, icon: PATH.bean },
  ];
  const fmt = (iso: string) => new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "ru-RU", { month: "short", year: "numeric" }).format(new Date(iso));
  return (
    <AccountShell section="passport" title={t("profile.passport")} crumbs={[[t("desktop.accTitle"), { name: "profile" }], [t("profile.passport")]]}>
      <div className="dx-pass-hero">
        <div>
          <small>{t("passport.eyebrow")}</small>
          <b className="dk-serif">{t("passport.title")}</b>
          <span>{t("passport.count", { count, total: ALL_ORIGINS.length })}</span>
          <i className="dx-pass-hero__bar"><em style={{ width: `${(count / ALL_ORIGINS.length) * 100}%` }} /></i>
        </div>
        <img src="/brand/kms-logo-white.png" alt="" onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = "none")} />
      </div>

      <div className="dac-sec"><h2 className="dk-serif">{t("passport.badges")}</h2></div>
      <div className="dx-badges">
        {badges.map((b) => (
          <div key={b.key} className="dac-card dx-badge" data-ok={b.ok}>
            {WI(b.icon, 46)}
            <b>{t(`passport.badge.${b.key}`)}</b>
            <small>{t(`passport.badgeHint.${b.key}`)}</small>
          </div>
        ))}
      </div>

      <div className="dac-sec"><h2 className="dk-serif">{t("passport.stamps")}</h2></div>
      {orders === null ? (
        <div className="dk-sk" style={{ height: 200, borderRadius: 16 }} />
      ) : (
        <div className="dx-stamps">
          {ALL_ORIGINS.map((o) => {
            const s = stamps.get(o.en);
            return (
              <div key={o.en} className="dx-stamp" data-ok={!!s} title={s?.product}>
                <span className="dx-stamp__ring"><b>{(o[locale] as string).slice(0, 2).toUpperCase()}</b></span>
                <b>{o[locale]}</b>
                <small>{s ? fmt(s.date) : t("passport.notYet")}</small>
              </div>
            );
          })}
        </div>
      )}
      {count === 0 && orders !== null && (
        <div className="dx-center"><button className="dk-btn" onClick={() => navigate({ name: "catalog", category: "coffee" })}>{t("passport.start")} {DI.arrow}</button></div>
      )}
    </AccountShell>
  );
}
